import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import InternalNavbar from '../../components/InternalNavbar';
import { toast } from 'react-toastify';
import { FaSpinner, FaLock, FaCrown, FaTimes } from 'react-icons/fa';
import api from '../../services/api';
import './lessons-iframe.css';

const IframeLessons = () => {
    const { levelParam } = useParams();
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const { subscription, hasActiveSubscription, isDemoSubscription, isPaidSubscription } = useSubscription();
    const iframeRef = useRef(null);

    const [iframeLoading, setIframeLoading] = useState(true);
    const [iframeError, setIframeError] = useState(false);
    const [clReady, setClReady] = useState(false);
    const [accessToken, setAccessToken] = useState(null);
    const [tokenLoading, setTokenLoading] = useState(true);
    const [levelData, setLevelData] = useState(null);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgradeModalData, setUpgradeModalData] = useState(null);

    // Content Layer course URL
    const CONTENT_BASE_URL = process.env.REACT_APP_CONTENT_URL || 'https://content.anasarabic.com';

    // Build course URL with token and level parameter
    const getCourseURL = () => {
        if (!levelData || !accessToken) return null;

        // Use CL's own ID system (cl_id)
        const courseId = levelData.cl_id;
        if (!courseId) {
            console.error('⚠️ No cl_id found in level data:', levelData);
            return null;
        }

        const COURSE_PATH = `/course/${courseId}`;

        return `${CONTENT_BASE_URL}${COURSE_PATH}?token=${accessToken}`;
    };

    const COURSE_URL = getCourseURL();

    // Check authentication
    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/signup', {
                state: {
                    action: 'course-access',
                    from: `/levels/${levelParam}/lessons`
                }
            });
        }
    }, [isAuthenticated, navigate, levelParam]);

    // Check if user needs email verification
    useEffect(() => {
        if (isAuthenticated && user && !user.is_verified) {
            toast.info('Please verify your email before accessing courses');
            navigate('/verify-email-notice');
        }
    }, [isAuthenticated, user, navigate]);

    // Fetch level data and content access token
    useEffect(() => {
        const fetchLevelAndToken = async () => {
            if (!isAuthenticated || !user) {
                return;
            }

            try {
                setTokenLoading(true);

                // First, fetch level data to get level details
                const levelResponse = await api.get(`/levels/level/${levelParam}`);
                const level = levelResponse.data;
                setLevelData(level);

                // Then, fetch content access token
                const tokenResponse = await api.post('/content/generate-token', {
                    content_id: level.id || levelParam,
                    content_type: 'course',
                    level_code: levelParam
                });

                const token = tokenResponse.data.access_token;
                setAccessToken(token);

            } catch (error) {
                // Check if it's a subscription error
                if (error.response?.status === 403) {
                    const errorCode = error.response?.data?.error?.code;

                    if (errorCode === 'SUBSCRIPTION_REQUIRED') {
                        toast.info('Please activate a subscription to access courses', {
                            autoClose: 5000
                        });
                        navigate('/demo-confirmation');
                    } else if (errorCode === 'SUBSCRIPTION_EXPIRED') {
                        toast.error('Your subscription has expired');
                        navigate('/demo-expired');
                    } else {
                        toast.error('Active subscription required to access courses');
                        navigate('/subscribe');
                    }
                } else if (error.response?.status === 404) {
                    toast.error('Level not found');
                    navigate('/levels');
                } else {
                    toast.error('Failed to load course. Please try again.');
                    setIframeError(true);
                }
            } finally {
                setTokenLoading(false);
            }
        };

        fetchLevelAndToken();
    }, [isAuthenticated, user, navigate, levelParam]);

    // Handle iframe load
    const handleIframeLoad = () => {
        setIframeError(false);

        // Add a timeout fallback in case CL doesn't send READY event
        setTimeout(() => {
            if (iframeLoading && !clReady) {
                setIframeLoading(false);
                toast.warning('Course loaded but not fully initialized', {
                    autoClose: 3000
                });
            }
        }, 10000); // 10 second timeout
    };

    // Handle iframe error
    const handleIframeError = () => {
        setIframeLoading(false);
        setIframeError(true);
        toast.error('Failed to load course. Please try again.');
    };

    // Listen for messages from the iframe (CL postMessage events)
    useEffect(() => {
        const handleMessage = (event) => {
            // Verify origin for security
            const allowedOrigins = [
                'https://content.anasarabic.com',
                'http://localhost:5173',  // CL dev server (Vite)
                'http://localhost:5000',
                'http://localhost:3001',
                'http://localhost:3000'   // BL dev server (for React DevTools)
            ];

            if (!allowedOrigins.includes(event.origin)) {
                return;
            }

            // Ignore React DevTools messages
            if (event.origin === 'http://localhost:3000' && !event.data?.type?.startsWith('CL_')) {
                return; // Silently ignore non-CL messages from same origin
            }

            // Handle different message types from CL
            switch (event.data.type) {
                case 'CL_COURSE_READY':
                case 'CL_READY':  // Support both event names for compatibility
                    // CL course is fully initialized and ready
                    setIframeLoading(false);
                    setClReady(true);
                    toast.success('Course loaded successfully!', {
                        autoClose: 2000,
                        position: 'top-center'
                    });
                    break;

                case 'CL_LESSON_STARTED':
                    // Lesson started - log progress
                    handleLessonStarted(event.data.data);
                    break;

                case 'CL_LESSON_COMPLETED':
                    // Lesson completed - save progress
                    handleLessonCompleted(event.data.data);
                    break;

                case 'CL_UPGRADE_REQUIRED':
                    // User tried to access locked content
                    handleUpgradeRequired(event.data.data);
                    break;

                case 'CL_SECTION_COMPLETED':
                    // Section completed
                    handleSectionCompleted(event.data.data);
                    break;

                case 'CL_PROGRESS_UPDATE':
                    // Progress update
                    // Future: Update progress indicators
                    break;

                case 'CL_COURSE_ERROR':
                    // CL encountered an error
                    toast.error(event.data.data?.message || 'An error occurred in the course');
                    break;

                // ==========================================
                // New Analytics Events from CL
                // ==========================================
                case 'CL_SESSION_STARTED':
                case 'CL_SESSION_HEARTBEAT':
                case 'CL_SESSION_ENDED':
                case 'CL_QUESTION_VIEWED':
                case 'CL_QUESTION_ANSWERED':
                case 'CL_QUESTION_SKIPPED':
                case 'CL_AUDIO_PLAYED':
                    // Forward analytics events to backend
                    handleAnalyticsEvent(event.data.type, event.data.data);
                    break;

                default:
                    // Log unknown CL events for debugging
                    if (event.data.type?.startsWith('CL_')) {
                        console.log('[BL] Unknown CL event:', event.data.type);
                    }
                    break;
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [levelParam]);

    // Handle analytics events from CL
    const handleAnalyticsEvent = async (eventType, eventData) => {
        console.log('[BL] Received analytics event from CL:', eventType, eventData);
        try {
            const response = await api.post('/content/track-analytics-event', {
                event_type: eventType,
                data: eventData,
                timestamp: new Date().toISOString()
            });
            console.log('[BL] Analytics event stored successfully:', eventType, response.data);
        } catch (error) {
            // Non-critical - log but don't show error to user
            console.error('[BL] Analytics event tracking failed:', eventType, error.message, error.response?.data);
        }
    };

    // Handle lesson started event
    const handleLessonStarted = async (lessonData) => {
        try {
            // Send to BL backend for tracking
            await api.post('/content/track-lesson-start', {
                course_id: lessonData.course_id,
                lesson_id: lessonData.lesson_id,
                lesson_title: lessonData.lesson_title,
                lesson_index: lessonData.lesson_index,
                level_code: levelParam
            });
        } catch (error) {
            // Non-critical, don't show error to user
        }
    };

    // Handle lesson completed event
    const handleLessonCompleted = async (lessonData) => {
        try {
            // Validate required fields
            if (!lessonData.lesson_id || typeof lessonData.score_percentage !== 'number') {
                throw new Error('Invalid lesson completion data');
            }

            // Save to BL backend
            await api.post('/content/save-lesson-progress', {
                course_id: lessonData.course_id,
                lesson_id: lessonData.lesson_id,
                lesson_title: lessonData.lesson_title,
                total_questions: lessonData.total_questions,
                completed_questions: lessonData.completed_questions,
                score_percentage: lessonData.score_percentage,
                time_spent_seconds: lessonData.time_spent_seconds,
                level_code: levelParam
            });

            // Show success message
            toast.success(`Lesson completed! Score: ${lessonData.score_percentage}%`, {
                autoClose: 3000
            });

        } catch (error) {
            toast.warning('Lesson completed, but failed to save progress.');
        }
    };

    // Handle upgrade required event
    const handleUpgradeRequired = (upgradeData) => {
        // Store upgrade data and show modal
        setUpgradeModalData(upgradeData);
        setShowUpgradeModal(true);

        // Also show a toast notification
        toast.info(
            `Lesson ${upgradeData.lesson_number} is locked. Upgrade to premium to continue!`,
            {
                autoClose: 3000
            }
        );
    };

    // Handle upgrade modal close
    const handleCloseUpgradeModal = () => {
        setShowUpgradeModal(false);
        setUpgradeModalData(null);
    };

    // Handle upgrade button click
    const handleUpgradeClick = () => {
        // Close modal
        handleCloseUpgradeModal();

        // Navigate to subscribe page
        navigate('/subscribe', {
            state: {
                from: `/levels/${levelParam}/lessons`,
                reason: 'locked_lesson',
                courseId: upgradeModalData?.course_id,
                lockedLesson: upgradeModalData?.lesson_number
            }
        });
    };

    // Handle section completed event
    const handleSectionCompleted = async (sectionData) => {
        try {
            // Save to BL backend
            await api.post('/content/save-section-progress', {
                course_id: sectionData.course_id,
                section_id: sectionData.section_id,
                section_title: sectionData.section_title,
                completed_lessons: sectionData.completed_lessons,
                total_lessons: sectionData.total_lessons,
                level_code: levelParam
            });

            // Show success message
            toast.success(`Section "${sectionData.section_title}" completed! 🎉`, {
                autoClose: 4000
            });

        } catch (error) {
            // Non-critical, don't show error to user
        }
    };

    if (!isAuthenticated || !user) {
        return null;
    }

    return (
        <div className="lessons-page iframe-lessons-page">
            <InternalNavbar />

            {/* Loading overlay - show while fetching token or loading iframe */}
            {(tokenLoading || (iframeLoading && !clReady)) && (
                <div className="iframe-loading">
                    <FaSpinner className="spinner" />
                    <p>
                        {tokenLoading ? 'Loading course...' : 'Initializing lessons...'}
                    </p>
                    <small>
                        {tokenLoading
                            ? 'Preparing your learning experience...'
                            : 'Waiting for content to load...'}
                    </small>
                </div>
            )}

            {/* Error overlay */}
            {iframeError && (
                <div className="iframe-error">
                    <p>Failed to load course. Please refresh the page or try again later.</p>
                    <button
                        className="btn-retry"
                        onClick={() => {
                            setIframeError(false);
                            setIframeLoading(true);
                            window.location.reload();
                        }}
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Iframe Content Section - Only render when token is ready */}
            {!tokenLoading && accessToken && COURSE_URL && (
                <div className="lessons-content-section">
                    <iframe
                        ref={iframeRef}
                        src={COURSE_URL}
                        title={`Arabic Course - ${levelData?.title || levelParam}`}
                        className={`course-iframe ${iframeLoading ? 'loading' : ''}`}
                        onLoad={handleIframeLoad}
                        onError={handleIframeError}
                        allow="fullscreen"
                        allowFullScreen
                        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                    />
                </div>
            )}

            {/* Upgrade Modal */}
            {showUpgradeModal && upgradeModalData && (
                <div className="upgrade-modal-overlay" onClick={handleCloseUpgradeModal}>
                    <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close-btn" onClick={handleCloseUpgradeModal}>
                            <FaTimes />
                        </button>

                        {/* Left Side - Icon */}
                        <div className="upgrade-modal-left">
                            <div className="modal-icon">
                                <FaLock />
                            </div>
                            <div className="icon-label">Premium Content</div>
                        </div>

                        {/* Right Side - Content */}
                        <div className="upgrade-modal-right">
                            <h2>Unlock This Lesson</h2>

                            <div className="modal-lesson-info">
                                <p className="lesson-title">{upgradeModalData.lesson_title}</p>
                                <p className="lesson-number">Lesson {upgradeModalData.lesson_number}</p>
                            </div>

                            <p className="modal-description">
                                {upgradeModalData.reason === 'navigation_blocked' && (
                                    'You\'ve completed the free lessons! Upgrade to premium to continue your learning journey.'
                                )}
                                {upgradeModalData.reason === 'lesson_locked' && (
                                    'This lesson is only available to premium members. Unlock all lessons with a premium subscription.'
                                )}
                                {upgradeModalData.reason === 'direct_access' && (
                                    'This content requires a premium subscription to access.'
                                )}
                            </p>

                            <div className="modal-benefits">
                                <h3><FaCrown /> What You Get</h3>
                                <ul>
                                    <li>✅ All lessons unlocked</li>
                                    <li>✅ Progress tracking</li>
                                    <li>✅ Certificates</li>
                                    <li>✅ Premium features</li>
                                </ul>
                            </div>

                            <div className="modal-actions">
                                <button className="btn-upgrade" onClick={handleUpgradeClick}>
                                    <FaCrown /> Upgrade Now
                                </button>
                                <button className="btn-maybe-later" onClick={handleCloseUpgradeModal}>
                                    Later
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default IframeLessons;
